import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useAgentMemory, FeedbackType, OutcomeType } from './useAgentMemory';

interface FeedbackState {
  interactionId: string;
  submitted: boolean;
  feedback: FeedbackType | null;
}

export function useAgentLearning() {
  const { user } = useAuth();
  const { learn } = useAgentMemory();
  const [feedbackStates, setFeedbackStates] = useState<Record<string, FeedbackState>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Submit feedback for an interaction
  const submitFeedback = useCallback(async (
    interactionId: string,
    feedback: FeedbackType,
    outcome?: OutcomeType
  ) => {
    if (!user || !interactionId) return false;

    setIsSubmitting(true);
    try {
      // Use the learn function from useAgentMemory
      await learn(interactionId, outcome || 'unknown', feedback);

      // Update local state
      setFeedbackStates(prev => ({
        ...prev,
        [interactionId]: {
          interactionId,
          submitted: true,
          feedback,
        },
      }));

      return true;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [user, learn]);

  // Quick positive feedback
  const thumbsUp = useCallback(async (interactionId: string) => {
    return submitFeedback(interactionId, 'positive', 'success');
  }, [submitFeedback]);

  // Quick negative feedback
  const thumbsDown = useCallback(async (interactionId: string) => {
    return submitFeedback(interactionId, 'negative', 'partial');
  }, [submitFeedback]);

  // Get feedback state for an interaction
  const getFeedbackState = useCallback((interactionId: string): FeedbackState | null => {
    return feedbackStates[interactionId] || null;
  }, [feedbackStates]);

  // Track outfit save/skip for learning
  const trackOutfitAction = useCallback(async (
    interactionId: string,
    action: 'saved' | 'skipped' | 'shared'
  ) => {
    if (!user) return;

    const outcome: OutcomeType = action === 'saved' || action === 'shared' ? 'success' : 'partial';
    const feedback: FeedbackType = action === 'saved' || action === 'shared' ? 'positive' : 'neutral';

    await learn(interactionId, outcome, feedback);

    // Store specific action memory
    try {
      await supabase.from('agent_memory').upsert({
        user_id: user.id,
        key: `outfit_action_${Date.now()}`,
        value: { action, interactionId, timestamp: new Date().toISOString() },
        memory_type: 'fact',
        source: 'explicit',
        confidence: 1.0,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,key' });
    } catch (error) {
      console.error('Failed to track outfit action:', error);
    }
  }, [user, learn]);

  // Track OOTD rating for learning
  const trackOOTDRating = useCallback(async (
    interactionId: string,
    score: number
  ) => {
    if (!user) return;

    const outcome: OutcomeType = 'success';
    const feedback: FeedbackType = score >= 70 ? 'positive' : score >= 50 ? 'neutral' : 'negative';

    await learn(interactionId, outcome, feedback);

    // Store rating trend
    try {
      await supabase.from('agent_memory').upsert({
        user_id: user.id,
        key: `ootd_rating_${Date.now()}`,
        value: { score, interactionId, timestamp: new Date().toISOString() },
        memory_type: 'fact',
        source: 'explicit',
        confidence: 1.0,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,key' });
    } catch (error) {
      console.error('Failed to track OOTD rating:', error);
    }
  }, [user, learn]);

  // Get learning stats via edge function
  const getLearningStats = useCallback(async () => {
    if (!user) return null;

    try {
      const { data, error } = await supabase.functions.invoke('agent-learning', {
        body: { action: 'get_learning_stats' },
      });

      if (error) throw error;
      return data?.stats;
    } catch (error) {
      console.error('Failed to get learning stats:', error);
      return null;
    }
  }, [user]);

  // Trigger behavior adaptation
  const adaptBehavior = useCallback(async () => {
    if (!user) return null;

    try {
      const { data, error } = await supabase.functions.invoke('agent-learning', {
        body: { action: 'adapt_behavior' },
      });

      if (error) throw error;
      return data?.adaptations;
    } catch (error) {
      console.error('Failed to adapt behavior:', error);
      return null;
    }
  }, [user]);

  return {
    // Feedback submission
    submitFeedback,
    thumbsUp,
    thumbsDown,
    getFeedbackState,
    
    // Action tracking
    trackOutfitAction,
    trackOOTDRating,
    
    // Learning analysis
    getLearningStats,
    adaptBehavior,
    
    // State
    isSubmitting,
  };
}
